import React, { useState } from 'react';
import { Users, X, Folder, GripVertical } from 'lucide-react';
import { SharedBoard } from '@/hooks/useSharedBoards';
import { Button } from '@/components/ui/button';

interface SharedBoardsModalProps {
  boards: SharedBoard[];
  onClose: () => void;
  onBoardDragStart?: (e: React.DragEvent, share: SharedBoard) => void;
}

export function SharedBoardsModal({ boards, onClose, onBoardDragStart }: SharedBoardsModalProps) {
  const [draggedBoardId, setDraggedBoardId] = useState<string | null>(null);

  const handleDragStart = (e: React.DragEvent, share: SharedBoard) => {
    setDraggedBoardId(share.id);
    e.dataTransfer.setData('application/json', JSON.stringify({
      type: 'shared_board',
      board: share
    }));
    e.dataTransfer.effectAllowed = 'copy';

    // Create drag preview
    const preview = document.createElement('div');
    preview.className = 'bg-background p-4 rounded-xl shadow-xl border border-border';
    preview.innerHTML = `<span class="font-medium">${share.board.name}</span>`;
    preview.style.position = 'absolute';
    preview.style.top = '-1000px';
    document.body.appendChild(preview);
    e.dataTransfer.setDragImage(preview, 50, 25);
    setTimeout(() => preview.remove(), 0);

    onBoardDragStart?.(e, share);
  };

  const handleDragEnd = () => {
    setDraggedBoardId(null);
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-background rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-border">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold text-foreground">Shared with me</h2>
          </div>
          <Button 
            variant="ghost" 
            size="icon"
            onClick={onClose}
            className="h-8 w-8"
          >
            <X className="w-5 h-5 text-muted-foreground" />
          </Button>
        </div>

        {/* Board list */}
        <div className="p-4 max-h-96 overflow-y-auto">
          <p className="text-sm text-muted-foreground mb-4">
            Drag a board onto the canvas to add it
          </p>

          {boards.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No boards have been shared with you yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {boards.map(share => (
                <div
                  key={share.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, share)}
                  onDragEnd={handleDragEnd}
                  className={`
                    flex items-center gap-3
                    p-3
                    bg-muted/50 hover:bg-muted
                    rounded-xl
                    cursor-grab active:cursor-grabbing
                    transition-colors
                    ${draggedBoardId === share.id ? 'opacity-50' : ''}
                  `}
                >
                  {/* Board icon */}
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: share.board.icon_color || '#6366F1' }}
                  >
                    <Folder className="w-5 h-5 text-white" />
                  </div>

                  {/* Board info */}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground truncate">{share.board.name}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      Shared by {share.shared_by_user?.email || 'Unknown'}
                    </p>
                  </div>

                  {/* Permission badge */}
                  <span className={`
                    px-2 py-1 text-xs font-medium rounded-full flex-shrink-0
                    ${share.permission === 'edit'
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                      : 'bg-muted text-muted-foreground'
                    }
                  `}>
                    {share.permission === 'edit' ? 'Can edit' : 'View only'}
                  </span>

                  {/* Drag indicator */}
                  <GripVertical className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
